import { Component, ElementRef, NgZone, OnInit, ViewChild, WritableSignal, computed, effect, inject, signal, viewChild } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { AddressService, NavegoAddressRequest } from '../../address.service';
import { Loader } from '@googlemaps/js-api-loader';
import { GoogleMapsUtils } from '../../common/utils';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { environment } from '../../../../environments/environment';
import { SignalsStoreService } from '../../signals-store.service';
import { LocalStorageService } from '../../local-storage.service';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { LOCALSTORAGE_KEYS } from '../../constants/databases';
import { MarketingReferral } from '../../types/common.types';
import { MatRadioButton } from '@angular/material/radio';
import { CustomerType, DeliveryOptionsResponseItem, ExplorerUserPayload, PickUpOption } from '../../types/account.types';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { AccountService } from '../../account.service';
import { finalize, firstValueFrom, tap } from 'rxjs';
import { CommonModule } from '@angular/common';
import { TurnstileComponent } from '../../turnstile/turnstile.component';
import { turnstilePendingMessage } from '../../constants/errors';
import { NotificationService } from '../../notification/notification.service';
import { isEmailValid } from '../../common/email-validator.util';
import { ModalContentService } from '../modal-content.service';
import { ModalContentTypes } from '../../constants/modal-content-types';

/** A day the visitor can be handed on `POST /explorer`. Zones and pickup
 *  locations describe their days differently; the selector only needs these. */
interface ExplorerDayOption {
  id: number;
  label: string;
}

@Component({
  selector: 'app-check-address',
  imports: [
    MatProgressBarModule,
    FormsModule,
    MatRadioButton,
    MatFormFieldModule,
    MatSelectModule,
    RouterLink,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TurnstileComponent,
  ],
  providers: [{
    provide: Loader,
    useValue: new Loader({
      apiKey: environment.apis.google.places,
      libraries: ['places'],
      region: 'US',
    })
  }],
  templateUrl: './check-address.component.html',
  styleUrl: './check-address.component.scss'
})
export class CheckAddressComponent implements OnInit {

  @ViewChild('autocompleteInput') autocompleteInput!: ElementRef<HTMLInputElement>;
  localStorageService = inject(LocalStorageService)

  private router = inject(Router);
  private activeModal = inject(NgbActiveModal)
  private addressService = inject(AddressService);
  private zone = inject(NgZone);
  private loader = inject(Loader);
  private signalsStoreService = inject(SignalsStoreService);
  private accountService = inject(AccountService);
  private modalContentService = inject(ModalContentService);
  #notificationService = inject(NotificationService);

  /** Explorer flow (DEV-11262). With this off the modal behaves exactly as it
   *  always has: check the address, then hand over to /signup. */
  readonly isExplorerEnabled = environment.config.flows.explorer.enabled;

  email = '';
  emailTouched = signal(false);
  isSubmitting = signal(false);

  /** Turnstile guards `POST /explorer` (DEV-11124). Tokens are single use. */
  turnstile = viewChild(TurnstileComponent);
  turnstileToken = signal<string | null>(null);

  /** The days on offer for whatever the visitor has chosen: the zone's on home
   *  delivery, the location's own on pickup. One list, so the selector does not
   *  have to know which branch it is on.
   *
   *  `dayId` is mandatory on `POST /explorer` either way, and the backend
   *  cancels the carts if it moves later, so it is asked for here rather than
   *  left to the signup (DEV-11262). */
  deliveryDays: WritableSignal<ExplorerDayOption[]> = signal([]);
  selectedDeliveryDayId: WritableSignal<number | null> = signal(null);
  isLoadingDeliveryDays = signal(false);
  #daysLoadedForZone: number | null = null;

  isGoogleAddressFilled = signal(false);
  isLoadingContent: any = computed(() => this.addressService.isLoading());
  hasDeliveryAddressCoverage: any = signal(false);

  membershipList: WritableSignal<CustomerType[]> = signal([]);
  selectedMembership: WritableSignal<CustomerType> = signal({ id: 0, name: '' });

  /** Fetches the zone's days as soon as an address comes back covered.
   *  An effect rather than part of `hasDeliveryAddressCoverage`, because that
   *  is a computed and this writes signals (NG0600). */
  #loadDaysOnCoverage = effect(() => {
    const zoneId = this.addressService.navegoAddress()?.zoneId;
    if (!this.isExplorerEnabled || !zoneId) return;
    if (!this.selectedMembership().configuration?.requireAddress) return;

    this.#loadDeliveryDays(zoneId);
  });

  /** Pickup days are the location's, not a zone's. */
  dayFieldLabel = computed(() =>
    this.selectedMembership().configuration?.requireAddress ? 'Delivery day' : 'Pickup day');

  pickUpOptions: WritableSignal<PickUpOption[]> = signal([]);
  selectedPickUpOption: WritableSignal<PickUpOption> = signal({ id: 0, name: '', deliveryDays: [], locationAddress: '' })

  isContentLoaded: WritableSignal<boolean> = signal(false);

  selectedPlace: google.maps.places.PlaceResult | null = null;

  queryString = ''

  isCheckingAddress = signal(false);

  ngOnInit(): void {
    // Set when an explorer is being resumed: their email is the one thing that
    // survives, since the backend returns nothing else about them (DEV-11262).
    const preloaded = this.localStorageService.get<string>(LOCALSTORAGE_KEYS.PRELOADED_EMAIL);
    if (preloaded) {
      this.email = preloaded;
      this.localStorageService.remove(LOCALSTORAGE_KEYS.PRELOADED_EMAIL);
    }

    this.getCustomerTypes();
    this.addressService.setFirstPickUpMembershipSelected$.pipe(tap(() => this.selectFirstPickUpMembership())).subscribe();
  }

  loginClickHandler() {
    this.activeModal.close();
    this.router.navigate(['/login']);
  }

  private onPlaceSelected(place: google.maps.places.PlaceResult) {
    this.zone.run(() => {
      const {
        city,
        stateCode,
        zipCode,
        name: street,
        latitude,
        longitude,
        formattedAddress,
        placeId
      } = this.addressService.setupDeliveryData(place);

      this.signalsStoreService.googleAddress.set({
        street,
        city,
        stateCode,
        zipCode,
        latitude,
        longitude,
        placeId,
      });

      this.selectedPlace = place;
    });


  }

  checkDeliveryAddressCoverage() {

    if (this.isCheckingAddress())
      return;

    this.isCheckingAddress.set(true);

    this.isGoogleAddressFilled.set(true);
    const { street, city, stateCode, latitude, longitude, zipCode } = this.signalsStoreService.googleAddress();
    const navegoAddressRequest = {
      street,
      city,
      state: stateCode,
      latitude,
      longitude,
      zipCode
    } as NavegoAddressRequest;

    this.addressService.checkDeliveryAddressByZipCode(navegoAddressRequest, true, true, this.isCheckingAddress);

    this.hasDeliveryAddressCoverage = computed(() => {
      if (!!this.addressService.navegoAddress()) {
        this.localStorageService.set(LOCALSTORAGE_KEYS.GOOGLEPLACERESULT, this.selectedPlace);
        // With the explorer flow on, a covered address is not the end of the
        // modal any more — it unlocks the email field so the visitor can start
        // shopping without leaving the page. Only the old behaviour hands off
        // to /signup here.
        //
        // The days are NOT fetched here: this is a computed, loading them
        // writes signals, and Angular refuses that with NG0600 — which took
        // the whole home-delivery branch down with it. `#loadDaysOnCoverage`
        // does it instead.
        if (!this.isExplorerEnabled) {
          setTimeout(() => {
            this.closeModal();
            this.router.navigate(['/signup'])
          }, 1000);
        }
      } else {
        this.localStorageService.remove(LOCALSTORAGE_KEYS.GOOGLEPLACERESULT);
      }

      return !!this.addressService.navegoAddress()
    });
  }

  //#region Explorer flow (DEV-11262)

  isEmailFilledIn(): boolean {
    return isEmailValid(this.email);
  }

  /** True once the visitor has given us everything `POST /explorer` needs.
   *  The day is mandatory on both branches. */
  isReadyToShop(): boolean {
    if (!this.selectedDeliveryDayId()) return false;

    if (this.selectedMembership().configuration?.requireAddress)
      return !!this.addressService.navegoAddress();

    return !!this.selectedPickUpOption()?.id;
  }

  setSelectedDeliveryDay(event: MatSelectChange) {
    this.selectedDeliveryDayId.set(event.value ?? null);
  }

  #loadDeliveryDays(zoneId?: number) {
    if (!zoneId || this.#daysLoadedForZone === zoneId) return;

    this.#daysLoadedForZone = zoneId;
    this.selectedDeliveryDayId.set(null);
    this.isLoadingDeliveryDays.set(true);

    this.accountService.getDeliveryOptions(zoneId).pipe(
      finalize(() => this.isLoadingDeliveryDays.set(false)),
      tap((options: DeliveryOptionsResponseItem[]) => {
        this.deliveryDays.set((options || []).map(o => ({
          id: Number(o.id),
          label: o.formattedName || o.name
        })));
        // One day is not a choice; pre-select it so the only thing left to do
        // is type the email.
        if (options?.length === 1) this.selectedDeliveryDayId.set(Number(options[0].id));
      })
    ).subscribe();
  }

  /** The pickup branch takes its days from the location rather than a zone, so
   *  there is nothing to fetch. The first one is the default the backend
   *  expects; the visitor can still move it before they start, which is the
   *  point of asking now rather than at the signup. */
  #setPickUpDeliveryDays(option: PickUpOption | null) {
    if (this.selectedMembership().configuration?.requireAddress) return;

    const days = option?.deliveryDays ?? [];
    this.#daysLoadedForZone = null;
    this.deliveryDays.set(days.map(d => ({ id: Number(d.id), label: d.name })));
    this.selectedDeliveryDayId.set(days.length ? Number(days[0].id) : null);
  }

  /**
   * Creates the explorer account and lets the visitor carry on shopping.
   *
   * The re-entrancy guard is not belt-and-braces: on Á Table a double click
   * here produced two accounts for one person (DEV-10545).
   */
  async startShopping() {
    if (this.isSubmitting()) return;

    this.emailTouched.set(true);
    if (!this.isEmailFilledIn() || !this.isReadyToShop()) return;

    if (TurnstileComponent.enabled && !this.turnstileToken()) {
      this.#notificationService.show({
        text: turnstilePendingMessage(this.turnstile()?.isInteractive()),
        type: 'warning'
      });
      return;
    }

    const email = this.email.trim();
    this.isSubmitting.set(true);

    try {
      const existing = await firstValueFrom(this.accountService.checkEmail(email));
      // An explorer's address is "in use" and still has to go through, because
      // resuming IS another `POST /explorer`: it hands back their session and
      // whatever carts are still open. Sending them to the login instead is a
      // dead end — they have no password, and the login answers
      // EXPLORER_NOT_ACTIVATED.
      if (existing?.emailUsed && !existing?.isAnonymousExplorer) {
        this.#openEmailAlreadyInUseModal(email);
        return;
      }

      const payload = this.#buildExplorerPayload(email);
      if (!payload) return;

      const response: any = await firstValueFrom(this.accountService.createExplorerAccount(payload));
      if (response?.error) return;

      this.activeModal.close({ explorerAccountCreated: true });
    } catch {
      // The request layer reports the failure. Leaving the modal open is the
      // point: the visitor keeps what they typed and can try again.
    } finally {
      this.isSubmitting.set(false);
      // The token was spent on this request whatever the outcome, so the widget
      // has to hand out a fresh one before the visitor can try again.
      this.turnstile()?.reset();
    }
  }

  #buildExplorerPayload(email: string): ExplorerUserPayload | null {
    const marketingReferralData = this.localStorageService.get<MarketingReferral>(LOCALSTORAGE_KEYS.MARKETING_REFERRAL);
    const withReferral = marketingReferralData && Object.keys(marketingReferralData).length
      ? { marketingReferralData }
      : {};
    const turnstileToken = this.turnstileToken() || undefined;
    const membership = this.selectedMembership();
    const customerTypeId = Number(membership.id) || undefined;
    const dayId = this.selectedDeliveryDayId();
    if (!dayId) return null;

    if (membership.configuration?.requireAddress) {
      const zone = this.addressService.navegoAddress();
      const google = this.signalsStoreService.googleAddress();
      if (!zone || !google) return null;

      return {
        email,
        customerTypeId,
        address: {
          street: google.street,
          city: google.city,
          stateCode: google.stateCode,
          zip: google.zipCode,
          latitude: google.latitude,
          longitude: google.longitude,
          zoneId: zone.zoneId
        },
        settings: { delivery: { dayId } },
        turnstileToken,
        ...withReferral
      };
    }

    // Pickup sends no address at all, and `customerTypeId` stops being optional:
    // the location is only meaningful against the membership it hangs off.
    const pickUp = this.selectedPickUpOption();
    if (!pickUp?.id || !customerTypeId) return null;

    return {
      email,
      customerTypeId,
      pickUpOptionId: pickUp.id,
      settings: { delivery: { dayId } },
      turnstileToken,
      ...withReferral
    };
  }

  #openEmailAlreadyInUseModal(email: string) {
    this.modalContentService.openModal(
      ModalContentTypes.CONFIRMATION,
      {
        title: 'Account Already Exists',
        textContent: `There's already an account for ${email}. Sign in and pick up where you left off.`,
        confirmButtonText: 'Sign In',
        cancelButtonText: 'Try a Different Email'
      }
    ).closed.subscribe((result: { confirm: boolean } | null) => {
      if (!result?.confirm) return;
      this.localStorageService.set(LOCALSTORAGE_KEYS.PRELOADED_EMAIL, email);
      this.closeModal();
      this.router.navigate(['/login']);
    });
  }

  //#endregion

  changeAddress() {
    this.hasDeliveryAddressCoverage = signal(false);
    this.isGoogleAddressFilled.set(false);
  }

  closeModal() {
    this.activeModal.close();
  }

  setSelectedPickUpOption(event: MatSelectChange) {
    const option = this.pickUpOptions().find(o => o.id === event.value);
    if (option) {
      this.selectedPickUpOption.set(option);
      this.localStorageService.set(LOCALSTORAGE_KEYS.SELECTED_PICKUP_OPTION, option);
      this.#setPickUpDeliveryDays(option);
    }
  }

  private getCustomerTypes() {
    this.accountService.getAllCustomerTypes().pipe(
      tap((response) => {
        if (!response?.data?.length) return;
        this.membershipList.set(JSON.parse(JSON.stringify(response.data)));
        const storedMembership: any = this.localStorageService.get(LOCALSTORAGE_KEYS.SELECTED_DELIVERY_PREFERENCE)
        let selectedMembership = response.data[0];
        if (storedMembership?.id)
          selectedMembership = response.data.find((m: any) => m.id === storedMembership.id) || response.data[0];

        // Whatever ends up selected, not only a remembered choice: the first
        // time round nothing is stored, so the locations were never fetched,
        // the select stayed empty and the button could not be pressed. Note
        // the property is `configuration`, singular — the old `configurations`
        // was always undefined, so this never filtered anything.
        if (!selectedMembership?.configuration?.requireAddress)
          this.getPickupLocations(selectedMembership.id);
        this.selectedMembership.set(JSON.parse(JSON.stringify(selectedMembership)));
        this.localStorageService.set(LOCALSTORAGE_KEYS.SELECTED_DELIVERY_PREFERENCE, selectedMembership);
        this.isContentLoaded.set(true);
        this.setUpAutocomplete();
      })
    ).subscribe()
  }

  setSelectedMembership(id: any) {
    if (!id) return;
    const selected = this.membershipList().find(m => m.id == id);
    if (!selected) return;
    // The branches do not share days, and sending one from the other is a 404.
    this.deliveryDays.set([]);
    this.selectedDeliveryDayId.set(null);
    this.#daysLoadedForZone = null;
    this.getPickupLocations(id);
    this.selectedMembership.set(JSON.parse(JSON.stringify(selected)));
    this.localStorageService.set(LOCALSTORAGE_KEYS.SELECTED_DELIVERY_PREFERENCE, selected);
    this.setUpAutocomplete();
  }

  private setUpAutocomplete() {
    if (this.selectedMembership().configuration?.requireAddress) {
      setTimeout(() => {
        GoogleMapsUtils.initializeAutocomplete(this.loader, this.autocompleteInput.nativeElement, this.onPlaceSelected.bind(this));
        const locationText: string | null =
          this.signalsStoreService.anonymousUserAddress()?.street ?? '';
        if (locationText) this.queryString = locationText;

        GoogleMapsUtils.initializeAutocomplete(this.loader, this.autocompleteInput.nativeElement, this.onPlaceSelected.bind(this));
        GoogleMapsUtils.loadAutoCompleteIfAny(
          this.loader,
          this.onPlaceSelected.bind(this),
          this.localStorageService
        );
      }, 1)
    }
  }

  private getPickupLocations(id: number) {
    this.accountService.getPickUpOption(id).pipe(
      tap((response) => {
        if (response?.length) {
          const r = response.map((l: { composedName: string; name: any; locationAddress: any; }) => {
            l.composedName = `${(l.name ? `${l.name} - ` : '') || ''}${l.locationAddress}`;
            return l;
          })
          this.pickUpOptions.set(r);
          this.selectedPickUpOption.set(r[0]);
          this.localStorageService.set(LOCALSTORAGE_KEYS.SELECTED_PICKUP_OPTION, r[0]);
          this.#setPickUpDeliveryDays(r[0]);
        }
      })
    ).subscribe()
  }

  goToSignup() {
    this.router.navigate(['/signup']);
    this.closeModal();
  }


  selectFirstPickUpMembership() {
    const selected = this.membershipList().find(m => !m.configuration?.requireAddress);
    if (!selected?.id) return;
    this.getPickupLocations(+selected.id);
    this.selectedMembership.set(JSON.parse(JSON.stringify(selected)));
    this.localStorageService.set(LOCALSTORAGE_KEYS.SELECTED_DELIVERY_PREFERENCE, selected);
  }

}
